import type { TFunction } from 'i18next'
import type { ReactNode } from 'react'

import { ApplePayMark } from '../icons/ApplePayMark'
import type { DepositAmountEntryConfig } from './depositFlowConfig'

/** The words and mark the amount screen draws for its rail. */
export interface DepositRailCopy {
  /** The submit's default label, before the server relabels a step. */
  submitLabel: string
  /** How the fee drawer names the rail; `undefined` leaves the summary out. */
  methodName: string | undefined
  /** Badge on the fee line's leading side. */
  methodBadge: ReactNode | undefined
}

function methodName(
  paymentMethod: string | undefined,
  t: TFunction,
): string | undefined {
  switch (paymentMethod) {
    case 'apple_pay':
      return t('paymentMethods.applePay')
    case 'card':
      return t('paymentMethods.card')
    case 'crypto':
      return t('selectMethod.crypto')
    default:
      return paymentMethod
  }
}

/**
 * Rail copy comes from the rail, so no host writes "Deposit with Apple Pay":
 * the SDK names the method in the user's locale and draws its mark. The
 * deprecated `submitLabel`, `methodName` and `methodBadge` still win while
 * they are set.
 */
export function resolveDepositRailCopy(
  config: DepositAmountEntryConfig,
  paymentMethod: string | undefined,
  t: TFunction,
): DepositRailCopy {
  const name = config.methodName ?? methodName(paymentMethod, t)
  const submitLabel =
    config.submitLabel ??
    (name === undefined
      ? t('depositAmount.submit')
      : t('depositAmount.submitWithMethod', { method: name }))
  const methodBadge =
    config.methodBadge !== undefined ? (
      config.methodBadge
    ) : paymentMethod === 'apple_pay' ? (
      <ApplePayMark />
    ) : undefined
  return { submitLabel, methodName: name, methodBadge }
}
